import React from 'react';
import {observer} from 'mobx-react';
import {classes, style as cn, stylesheet} from 'typestyle';
import {ColorHelper, px} from 'csx';

import {LoadingHover} from './TermItem';
import {KPI} from '../../kpi';
import {getReadableTextColor} from '../../utility/ColorUtils';
import {BlackColorFaded20} from '../../styles/colors';

interface GroupedTermItemProps {
    removeTermFromDiagram: (
        searchtermOrProductId: string,
        channelName: string,
        kpi: KPI,
        isChannelDiagram?: boolean,
        testId?: string,
        variantId?: string
    ) => void;
    getLabelForKpi: (kpi: KPI) => string;
    checkIfGraphIsLoaded: (searchtermOrProductId: string, channelName: string, kpi: string, testId?: string, variantId?: string) => boolean;
    checkIfGraphIsSelected: (
        searchtermOrProductId: string,
        channelName: string,
        kpi: string,
        noSelectedEqualsTrue: boolean,
        testId?: string,
        variantId?: string
    ) => boolean;
    selectGraph: (searchtermOrProductId: string, channelName: string, kpi: KPI, testId?: string, variantId?: string) => void;
    getDiagramColor: (channelName: string, searchtermOrProductId: string, kpi: string, testId?: string, variantId?: string) => ColorHelper;
    searchtermOrPid?: string;
    kpi: KPI;
    isChannel?: boolean;
    availableChannelNames?: string[];
    abTestData?: {
        availableVariants: string[];
        channelName: string;
        testId: string;
    };
}

export const hoverButtonClassName = cn({
    cursor: 'pointer',
    borderRadius: 2,
    display: 'inline-block',
    width: 20,
    height: 20,
    textAlign: 'center',
    lineHeight: px(20),
    $nest: {
        '&:hover': {
            backgroundColor: BlackColorFaded20,
        },
    },
});

@observer
export class GroupedTermItem extends React.Component<GroupedTermItemProps, {}> {
    private removeGroupedItemsFromDiagram = () => {
        const {availableChannelNames, abTestData} = this.props;
        if (availableChannelNames) {
            availableChannelNames.forEach((channelName) => {
                const titleToUse = this.props.isChannel ? channelName : this.props.searchtermOrPid || '';
                this.props.removeTermFromDiagram(titleToUse, channelName, this.props.kpi, this.props.isChannel);
            });
        } else if (abTestData) {
            abTestData.availableVariants.forEach((variantId) => {
                const titleToUse = this.props.isChannel ? abTestData.channelName : this.props.searchtermOrPid || '';
                this.props.removeTermFromDiagram(
                    titleToUse,
                    abTestData.channelName,
                    this.props.kpi,
                    this.props.isChannel,
                    abTestData.testId,
                    variantId
                );
            });
        }
    };

    private renderChannelBoxes(availableChannelNames: string[]) {
        return availableChannelNames.map((channelName) => {
            const searchtermOrPid = this.props.isChannel ? channelName : this.props.searchtermOrPid || '';
            return (
                <GroupBox
                    key={channelName}
                    backgroundColor={this.props.getDiagramColor(channelName, searchtermOrPid, this.props.kpi)}
                    text={channelName}
                    searchtermOrPid={searchtermOrPid}
                    kpi={this.props.kpi}
                    channelName={channelName}
                    isLoading={!this.props.checkIfGraphIsLoaded(searchtermOrPid, channelName, this.props.kpi)}
                    isSelected={this.props.checkIfGraphIsSelected(searchtermOrPid, channelName, this.props.kpi, true)}
                    selectGraph={() => this.props.selectGraph(searchtermOrPid, channelName, this.props.kpi)}
                />
            );
        });
    }

    private renderVariantIdBoxes(abTestData: {availableVariants: string[]; channelName: string; testId: string}): React.ReactNode[] {
        const {availableVariants, channelName, testId} = abTestData;
        const {kpi} = this.props;
        const searchtermOrPid = this.props.isChannel ? channelName : this.props.searchtermOrPid || '';
        return availableVariants.map((variantId) => {
            return (
                <GroupBox
                    key={variantId}
                    backgroundColor={this.props.getDiagramColor(channelName, searchtermOrPid, kpi, testId, variantId)}
                    text={variantId}
                    searchtermOrPid={searchtermOrPid}
                    kpi={kpi}
                    channelName={channelName}
                    isLoading={!this.props.checkIfGraphIsLoaded(searchtermOrPid, channelName, kpi, testId, variantId)}
                    isSelected={this.props.checkIfGraphIsSelected(searchtermOrPid, channelName, kpi, true, testId, variantId)}
                    selectGraph={() => this.props.selectGraph(searchtermOrPid, channelName, kpi, testId, variantId)}
                />
            );
        });
    }

    public render() {
        const kpiName = this.props.getLabelForKpi(this.props.kpi);
        const {abTestData} = this.props;

        const boxesToRender = ((): React.ReactNode[] => {
            if (this.props.availableChannelNames) {
                return this.renderChannelBoxes(this.props.availableChannelNames);
            } else if (this.props.abTestData) {
                return this.renderVariantIdBoxes(this.props.abTestData);
            }
            return [];
        })();

        const searchterOrPid = this.props.searchtermOrPid === '' ? '""' : this.props.searchtermOrPid;

        return (
            <div className={itemCss.container}>
                <div className={itemCss.closer}>
                    <div onClick={this.removeGroupedItemsFromDiagram} className={hoverButtonClassName}>
                        <i className="fa fa-times" aria-hidden="true" />
                    </div>
                </div>
                <div className={itemCss.itemBody}>
                    <div className={itemCss.attributeContainer}>
                        {searchterOrPid ? (
                            <React.Fragment>
                                <div className={itemCss.attributeLine}>{searchterOrPid}</div>
                                <div className={classes(itemCss.attributeLine, 'bold')}>{kpiName}</div>
                            </React.Fragment>
                        ) : (
                            <div className={classes(itemCss.attributeLine, 'bold')}>{kpiName}</div>
                        )}
                        {abTestData ? <div className={itemCss.attributeLine}>{abTestData.channelName}</div> : null}
                    </div>
                    <div className={itemCss.boxContainer}>{boxesToRender}</div>
                </div>
            </div>
        );
    }
}

interface ChannelBoxProps {
    backgroundColor: ColorHelper;
    text: string;
    searchtermOrPid: string;
    kpi: string;
    channelName: string;
    isLoading: boolean;
    isSelected: boolean;
    selectGraph: () => void;
}

const GroupBox: React.FunctionComponent<ChannelBoxProps> = (props) => {
    return (
        <div
            onClick={() => props.selectGraph()}
            className={itemCss.channelBox}
            style={{
                opacity: props.isSelected ? 1 : 0.2,
                color: getReadableTextColor(props.backgroundColor),
                backgroundColor: props.backgroundColor.toString(),
            }}>
            {props.isLoading ? <LoadingHover /> : null}
            {props.text}
        </div>
    );
};

const itemCss = stylesheet({
    boxContainer: {
        marginLeft: 15,
    },
    channelBox: {
        position: 'relative',
        height: 40,
        paddingLeft: 10,
        paddingRight: 10,
        minWidth: 40,
        marginRight: 5,
        display: 'inline-block',
        verticalAlign: 'top',
        textAlign: 'center',
        lineHeight: px(40),
        borderRadius: 2,
    },
    container: {
        position: 'relative',
        height: 50,
        paddingRight: 20,
        minWidth: 100,
        display: 'inline-block',
        marginRight: 5,
        paddingLeft: 5,
        verticalAlign: 'top',
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid ' + BlackColorFaded20,
    },
    closer: {
        position: 'absolute',
        right: 2,
        top: 0,
        zIndex: 29,
    },
    attributeLine: {
        fontSize: 12,
        $nest: {
            '&.bold': {
                fontWeight: 'bold',
            },
        },
    },
    attributeContainer: {
        display: 'flex',
        flexDirection: 'column',
        lineHeight: px(14),
    },
    itemBody: {
        display: 'flex',
        height: '100%',
        alignItems: 'center',
    },
});



// WEBPACK FOOTER //
// ./src/views/Legend/GroupedTermItem.tsx